import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "antd/lib/table";
import axios, { AxiosError, AxiosResponse } from "axios";
import { Button, Col, Row, Space, Tooltip } from "antd";
import {
  ControlOutlined,
  EditOutlined,
  HistoryOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { parseISO } from "date-fns";
import Table from "../../components/Table";
import { Container } from "./style";
import api from "../../services/api";
import { useToast } from "../../hooks/toast";
import OpexModal from "./OpexModal";
import ConfigModal from "./ConfigEditModal";
import FieldValueHistoryModalOpex from "./FieldValueHistoryModalOpex";
import Grafic from "./Grafic";
import Stat from "../../components/Stat";
import EditFieldOpex from "../../components/OpexActions/edit";

export interface IOpexs {
  id: string;
  planned_opex: number;
  realized_opex: number;
  trimester: 1 | 2 | 3 | 4;
  year: number;
  create_at: string;
  create_date: string;
}

export interface IConfig {
  id: string;
  name: string;
  value: number;
  user_id: string;
  create_at: string;
  history_config: IhistoryConfig[];
}

export interface IhistoryConfig {
  id: string;
  m_value: number;
  m_user_id: string;
  config_id: string;
  name: string;
  create_at: string;
  update_at: string;
}

const Opex: React.FC = (): JSX.Element => {
  const [opexs, setOpexs] = useState<IOpexs[]>([]);
  const [config, setConfig] = useState<IConfig[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalVisibleConfig, setModalVisibleConfig] = useState<boolean>(false);
  const [editedOpex, setEditedOpex] = useState<IOpexs | undefined>();
  const [editedConfig, setEditedConfig] = useState<IConfig | undefined>();
  const [historyConfig, setHistoryConfig] = useState<IhistoryConfig[]>([]);
  const { t } = useTranslation(["config"]);
  const { addToast } = useToast();
  const handleCloseModal = useCallback(
    (opex?: IOpexs[]) => {
      setEditedOpex(undefined);
      if (opex) {
        setOpexs([...opex, ...opexs]);
        // eslint-disable-next-line no-shadow
      }
      setModalVisible(false);
    },
    [opexs]
  );
  const handleCloseModalConfig = useCallback(
    // eslint-disable-next-line no-shadow
    (config?: IConfig[]) => {
      setEditedConfig(undefined);
      if (config) {
        setConfig(config);

        if (config[0]?.history_config) {
          const his = config[0]?.history_config;
          setHistoryConfig([his[his?.length - 1], his[his?.length - 2]]);
        }
      }
      setModalVisibleConfig(false);
    },
    [config]
  );
  const handleConfig = useCallback((conf: IConfig[]) => {
    setConfig(conf);
    if (conf[0]?.history_config) {
      const his = conf[0]?.history_config;
      setHistoryConfig(his);
    }
  }, []);
  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined, isDeleting?: boolean) => {
      let description;

      switch (response?.status) {
        case 403:
          description = t("requestError.403");
          break;
        case 401:
          description = t("requestError.401");
          break;
        case 404:
          description = t("requestError.404");
          break;
        default:
          description = isDeleting
            ? t("requestError.delete")
            : t("requestError.get");
      }

      addToast({
        type: "error",
        title: isDeleting
          ? t("requestError.deleteStatus")
          : t("requestError.getStatus"),
        description,
      });
    },
    [addToast, t]
  );

  useEffect(() => {
    setLoading(true);
    api
      .get<IOpexs[]>("/opexs", {
        params: {
          name: undefined,
        },
      })
      .then((response) => {
        setOpexs(response.data);
      })
      .catch((err) => {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        }
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    setLoading(true);
    api
      .get<IConfig>("/configurations", {
        params: {
          name: "PLANNED_OPEX",
        },
      })
      .then((response) => {
        setConfig([response.data]);
        const his = response.data.history_config as IhistoryConfig[];
        setHistoryConfig(his);
      })
      .catch((err) => {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setConfig]);
  const handleEditOpex = useCallback((opex: IOpexs) => {
    if (opex.create_at !== "Não Criado") {
      setEditedOpex(opex);
    }
    setModalVisible(true);
  }, []);

  const handleEditConfig = useCallback((configE: IConfig) => {
    if (configE) {
      setEditedConfig(configE);
    }
    setModalVisibleConfig(true);
  }, []);

  const columnsOpexs: ColumnsType<IOpexs> = [
    {
      title: t("opexs.planned_opex"),
      dataIndex: "planned_opex",
    },
    {
      title: t("opexs.realized_opex"),
      dataIndex: "realized_opex",
    },
    {
      title: t("opexs.trimester"),
      dataIndex: "trimester",
    },
    {
      title: t("opexs.year"),
      dataIndex: "year",
    },
    {
      title: "Ações",
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            type="link"
            onClick={() => handleEditOpex(record)}
          />
        </>
      ),
    },
  ];
  const extraAction = useMemo(() => {
    return (
      <Tooltip title={t("modalAddConfig.add")}>
        <Button
          icon={<PlusCircleOutlined />}
          type="link"
          size="large"
          onClick={() => setModalVisibleConfig(true)}
        />
      </Tooltip>
    );
  }, [t]);
  const extraActionOpex = useMemo(() => {
    return (
      <Tooltip title={t("modalOpex.add")}>
        <Button
          icon={<PlusCircleOutlined />}
          type="link"
          size="large"
          onClick={() => setModalVisible(true)}
        />
      </Tooltip>
    );
  }, [t]);
  const columnsConfig: ColumnsType<IConfig> = [
    {
      title: t("config.name"),
      dataIndex: "name",
    },
    {
      title: t("config.value"),
      dataIndex: "value",
    },
    {
      title: t("config.create_by"),
      dataIndex: "user_id",
    },
    {
      title: t("opexs.date_create"),
      dataIndex: "create_at",
      render: (date_create: string) => {
        return parseISO(date_create.split("T")[0]).toLocaleDateString("pt-BR");
      },
    },
    {
      title: "Ações",
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            type="link"
            onClick={() => handleEditConfig(record)}
          />
          <Button
            icon={<HistoryOutlined />}
            type="link"
            onClick={() => setOpenModal(true)}
          />
        </>
      ),
    },
  ];
  return (
    <Container>
      <OpexModal
        visible={modalVisible}
        handleCloseModal={handleCloseModal}
        opex={editedOpex}
      />
      <ConfigModal
        visible={modalVisibleConfig}
        handleCloseModal={handleCloseModalConfig}
        config={editedConfig}
      />
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={8}>
            <Stat
              title={t("opexs.planned_opex")}
              value={`${config[0]?.value ? config[0].value : ""}`}
              icon={<ControlOutlined />}
              loading={loading}
              editComponent={
                config ? (
                  <EditFieldOpex
                    fieldName="EditFieldOpex"
                    opexUpdate={config[0]}
                    handleConfig={(conf) => handleConfig(conf)}
                    title={t("opexs.planned_opex")}
                  />
                ) : (
                  <></>
                )
              }
              valueHistoryComponent={
                historyConfig ? (
                  <FieldValueHistoryModalOpex
                    configData={historyConfig as IhistoryConfig[]}
                    title={t("config.title")}
                  />
                ) : (
                  <></>
                )
              }
            />
          </Col>
        </Row>
      </Space>
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        <Grafic update={opexs} />
      </Space>
      <Table
        data={opexs || []}
        columns={columnsOpexs}
        title={<>{t("opexs.title")}</>}
        extraActions={extraActionOpex}
        footer={() => <></>}
        loading={loading}
        pagination={false}
        extraFilters={<></>}
      />
    </Container>
  );
};

export default Opex;
